import i18next from 'i18next';
import mime from 'browserify-mime';
import {Directory, Filesystem} from '@capacitor/filesystem';
import {Camera, CameraResultType, CameraSource} from '@capacitor/camera';
import {Capacitor} from '@capacitor/core';

import * as GlobalEvent from 'web-app/services/global-event';
import PlatformHelper from 'web-app/helpers/platform-helper';
import * as SentryService from 'web-app/services/sentry';
import * as CordovaPlugins from 'web-app/util/capacitor';
import {StorageHelperPlugin} from 'web-app/custom-capacitor-plugins/storage-plugin';
import * as ArrayUtil from 'web-app/util/array';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {ForegroundServicePlugin} from 'web-app/custom-capacitor-plugins/foreground-service-plugin';
import {getWhitelabelName} from 'web-app/shared/white-labeling-helpers';
import LocalStorage from 'web-app/services/local-storage';

import * as Constants from './constants';
import type * as Types from './types';
import {blobToFile} from './typed-helpers';

const ignorredErrorMessages = [
    // https://github.com/ionic-team/capacitor-plugins/blob/f2a70219bafbcbe136758c10bb43746f95a9e336/camera/android/src/main/java/com/capacitorjs/plugins/camera/CameraPlugin.java#L331
    'No image picked',
    // https://github.com/ionic-team/capacitor-plugins/blob/f2a70219bafbcbe136758c10bb43746f95a9e336/camera/android/src/main/java/com/capacitorjs/plugins/camera/CameraPlugin.java#L401
    'No images picked',
    // https://github.com/ionic-team/capacitor-plugins/blob/f2a70219bafbcbe136758c10bb43746f95a9e336/camera/src/web.ts#L68
    'User cancelled photos app',
];

export const getFile = (
    filePath: string,
    fileExtensions: string[],
    addFile: (file: Types.CapacitorFile, allFiles?: File[] | FileList) => void,
    maxFileSize: number,
) => {
    const filename = ArrayUtil.last(filePath.split('/'));

    if (!hasValue(filename)) {
        GlobalEvent.updateError(i18next.t('upload.errorImageCouldNotBeLoaded'));
        return;
    }

    const extension = ArrayUtil.last(filename.split('.'));

    if (!hasValue(extension) || !fileExtensions.includes(`.${extension}`)) {
        GlobalEvent.updateError(i18next.t('upload.errorFiletypeNotAllowed', {filename}));
        return;
    }

    urlToBlob(Capacitor.convertFileSrc(filePath))
        .then(blob => {
            const file = blobToFile(blob, filename, mime.lookup(filename)) as Types.CapacitorFile;
            file.id = filename;
            file.localSrc = Capacitor.convertFileSrc(filePath);

            if (file.size > maxFileSize) {
                GlobalEvent.updateError(
                    i18next.t('upload.errorExceedMaximumFilesize', {
                        filename: file.name,
                        maximumFilesize: `${maxFileSize / 1024e3} MB`,
                    }),
                );
                return;
            }

            addFile(file);
        })
        .catch(error => {
            GlobalEvent.updateError(i18next.t('upload.errorImageCouldNotBeLoaded'));
            SentryService.captureException(error);
        });
};

const urlToBlob = (url: string): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open('GET', url, true);
        request.responseType = 'blob';
        request.onload = function () {
            resolve(request.response);
        };
        request.onerror = function () {
            reject(new Error(`Unable to read file from url ${url}`));
        };
        request.send();
    });
};

export const androidPictureHandler = async (
    fileExtensions: string[],
    addFile: (file: Types.CapacitorFile, allFiles?: File[] | FileList) => void,
    maxFileSize: number,
) => {
    const shouldUseForegroundService = LocalStorage.getForUserUnencrypted(
        Constants.USE_ANDROID_FOREGROUND_SERVICE_WHILE_USING_CAMERA_EXPERIMENT,
    );

    if (shouldUseForegroundService) {
        try {
            await ForegroundServicePlugin.startForegroundService({title: getWhitelabelName()});
        } catch (error) {
            SentryService.captureException(error);
        }
    }

    try {
        const {path} = await Camera.getPhoto({
            resultType: CameraResultType.Uri,
            source: CameraSource.Camera,
            correctOrientation: true,
        });

        if (!hasValue(path)) {
            return Promise.reject(new Error('No image picked'));
        } else {
            return getFile(path, fileExtensions, addFile, maxFileSize);
        }
    } catch (error) {
        if (!ignorredErrorMessages.includes(error.message)) {
            GlobalEvent.updateError(i18next.t('upload.errorDuringUpload'));
            SentryService.captureException(error);
        }
        return undefined;
    } finally {
        if (shouldUseForegroundService) {
            ForegroundServicePlugin.stopForegroundService();
        }
    }
};

export const androidPhotoGalleryHandler = (
    allowMultiple: boolean,
    fileExtensions: string[],
    addFile: (file: Types.CapacitorFile, allFiles?: File[] | FileList) => void,
    maxFileSize: number,
) => {
    return Camera.pickImages({
        correctOrientation: true,
        limit: allowMultiple ? undefined : 1,
    })
        .then(result => {
            return Promise.all(
                result.photos
                    .map(photo => photo.path) // If photo has no path we can't handle it
                    .filter(hasValue)
                    .map(path => getFile(path, fileExtensions, addFile, maxFileSize)),
            );
        })
        .catch((error: Error) => {
            if (!ignorredErrorMessages.includes(error.message)) {
                GlobalEvent.updateError(i18next.t('upload.errorDuringUpload'));
                SentryService.captureException(error);
            }
        });
};

export const androidVideoHandler = (
    fileExtensions: string[],
    addFile: (file: Types.CapacitorFile, allFiles?: File[] | FileList) => void,
    maxFileSize: number,
) => {
    CordovaPlugins.androidVideoHandler()
        .then(mediaFiles => {
            mediaFiles.forEach(file => {
                getFile(file.fullPath, fileExtensions, addFile, maxFileSize);
            });
        })
        .catch(error => {
            // Solution for https://famlyapp.atlassian.net/browse/FBS-7571
            if (error?.code === CordovaPlugins.captureError.CAPTURE_PERMISSION_DENIED) {
                GlobalEvent.updateError(i18next.t('react.components.fileUpload.translations.json.permissionDenied'));
                return;
            }

            // User cancelled
            if (!error || error.code !== CordovaPlugins.captureError.CAPTURE_NO_MEDIA_FILES) {
                GlobalEvent.updateError(i18next.t('upload.errorDuringUpload'));
                SentryService.captureException(error);
            }
        });
};

export const getExtensionFromFilename = (filename: string) => {
    const split = filename.split('.');
    return (split[split.length - 1] || '').toLowerCase();
};

export const getExtension = (file: Types.CustomFile) => {
    const extension = mime.extension(file.type);
    if (!extension) {
        return getExtensionFromFilename(file.name);
    }
    return extension.toLowerCase();
};

const MAX_FILE_SIZE = Constants.MAX_FILE_SIZE;

interface ProcessFilesInput {
    files: FileList | null;
    multiple: boolean;
    allowedFileExtensions: string[];
    onFileLoad: (file: Types.CustomFile, allFiles: File[] | FileList) => void;
}

export const processFiles = ({files, multiple, allowedFileExtensions, onFileLoad}: ProcessFilesInput) => {
    if (files === null) {
        return;
    }

    // files is a FileList, and we need an array
    // IE Can't use Object.values here, so we map from keys
    const filteredFiles = Object.keys(files)
        .map<Types.CustomFile>(key => {
            return files[key];
        })
        .filter(file => {
            // Workaround for weird behavior on iOS
            if (typeof file !== 'object') {
                return false;
            }

            if (file.size === 0) {
                GlobalEvent.updateError(i18next.t('upload.errorEmptyFile', {filename: file.name}));
                return false;
            }

            return true;
        });

    filteredFiles.forEach(file => {
        const extension = getExtension(file);

        /**
         * When persisting files on the db, we usually store the objectUrl for the file, which has a max length of 256 characters.
         * Since the file name is part of the objectUrl, we need to limit it to make sure that we don't hit that amount of characters.
         *
         * The longest prefix is about 125 chars currently, so here we allow file names up to 110 chars,
         * to have some buffer in case the bucket url, or the objectKey pattern, change without the FE knowing.
         */
        if (file.name.length > 110) {
            GlobalEvent.updateError(
                i18next.t('react.components.fileUpload.translations.json.errorFilenameTooLong', {filename: file.name}),
            );
            return;
        }

        if (!allowedFileExtensions.includes(`.${extension}`)) {
            GlobalEvent.updateError(i18next.t('upload.errorFiletypeNotAllowed', {filename: file.name}));
        } else if (file.size > MAX_FILE_SIZE) {
            GlobalEvent.updateError(
                i18next.t('upload.errorExceedMaximumFilesize', {
                    filename: file.name,
                    maximumFilesize: `${MAX_FILE_SIZE / 1024e3} MB`,
                }),
            );
        } else if (PlatformHelper.isIos() && file.type.match('video') && multiple) {
            // See if file can be read (in case it's not selected from the photo library)
            file.id = file.id || file.name;
            const fileReader = new FileReader();
            fileReader.onload = () => {
                onFileLoad(file, files);
            };
            fileReader.onerror = () => {
                GlobalEvent.updateError(i18next.t('upload.errorWithSelectedFile', {filename: file.name}));
            };
            fileReader.readAsDataURL(file);
        } else {
            file.id = file.name === 'image.jpeg' ? `${new Date().getTime()}.jpg` : file.name;
            onFileLoad(file, filteredFiles);
        }
    });
};

/**
 * This function clears sensitive files potentially stored in the cache directory of the app. Features
 * that create sensitive material on the device should also be responsible for cleaning up,
 * and can use this function to do so. Feel free to extend this function to help you clear the
 * cache of anything that shouldn't be persisted.
 *
 * This function is (and should be) run every time the app starts. This is done in case where the
 * user closes the app, or it crashed, we have this as a fail-safe mechanism to ensure that we
 * flush the sensitive material.
 *
 * Currently it is used to remove images taken with the cameraPreview plugin
 * for the "Scan documents" feature. Images taken with this plugin will always start with "cpcp"
 */
export const cleanDocumentScannerFiles = () => {
    if (!PlatformHelper.isMobileApp()) {
        return Promise.resolve();
    }

    if (PlatformHelper.isAndroid()) {
        return Filesystem.readdir({path: '/', directory: Directory.Cache})
            .then(({files}) => {
                const cameraPreviewImages = files.filter(file => file.name.startsWith('cpcp'));
                return Promise.all(
                    cameraPreviewImages.map(image => {
                        Filesystem.deleteFile({
                            path: image.name,
                            directory: Directory.Cache,
                        }).catch(error => {
                            SentryService.captureMessage('Failed to delete file in the Capacitor cache directory', {
                                extra: {
                                    error,
                                },
                            });
                        });
                    }),
                );
            })
            .catch(error => {
                SentryService.captureMessage('Failed to read the Capacitor cache directory', {
                    extra: {
                        error,
                    },
                });
            });
    } else if (PlatformHelper.isIos()) {
        return StorageHelperPlugin.cleanDocumentScannerFiles();
    } else {
        return Promise.resolve();
    }
};

/*
|---------------------------------------------------------------------------------
| PDF generation
|---------------------------------------------------------------------------------
*/

// A4 = 210 x 297 (millimiters)
const A4_PAPER_SIZE = {
    width: 210,
    height: 297,
};

export const generatePDF = async (images: string[]) => {
    const jspdf = await import('jspdf');

    try {
        // Default export is A4 paper, portrait, using millimeters for units
        const doc = new jspdf.jsPDF();
        doc.addImage(images[0], 'JPEG', 0, 0, A4_PAPER_SIZE.width, A4_PAPER_SIZE.height);

        images.slice(1).forEach(image => {
            doc.addPage();
            doc.addImage(image, 'JPEG', 0, 0, A4_PAPER_SIZE.width, A4_PAPER_SIZE.height);
        });

        const fileId = `${new Date().getTime()}.pdf`;
        const pdfFile = blobToFile(doc.output('blob'), fileId, 'application/pdf');
        (pdfFile as Types.CustomFile).id = fileId;

        return pdfFile;
    } catch (error) {
        throw new Error(error.message);
    }
};
